import * as THREE from 'three/webgpu';
import { SketchRelationKind } from '../../sketch/solver/constraint/relation-compatibility/SketchRelationKinds';
import {
    AbstractSketchEdgeObject,
    FeatureFace,
    FeatureLine,
    FeaturePoint,
    isFeatureFace,
    isFeatureLine,
    isFeaturePoint,
    isSketchArcObject,
    isSketchCircleObject,
    isSketchContour,
    isSketchEllipseObject,
    isSketchLineObject,
    isSketchParabolaObject,
    isSketchPointObject,
    isSketchSplineObject,
    isSketchStyleSplineObject,
    SketchArcObject,
    SketchCircleObject,
    SketchEllipseObject,
    SketchLineObject,
    SketchParabolaObject,
    SketchPointKind,
    SketchPointObject,
    SketchSplineObject,
    SketchStyleSplineObject,
} from '../../mesh';
import { FeatureResultMesh, isFeatureResultMesh } from '../../mesh/feature/FeatureResultMesh';
import {
    createFeatureFaceMaterial,
    FeatureFaceMaterialState,
    isDashDotLIneNodeMaterial,
    LineMaterialFactory,
    LineMaterialState,
    PointsMaterialFactory,
    SketchEdgeBaseStyle,
    syncThinChainLineGeometry,
    type SketchLineMaterial,
} from '../../material';
import {
    disposeOwnedMaterial,
    markSharedInteractionMaterial,
} from '../../material/materialOwnership';
import { isDatumPoint } from '../../reference/DatumPoint';
import { isSketchEntityReferenceSourceHighlight } from '../../reference/sketch-entity-reference/command/selection/SketchEntityReferenceSourceHighlightMarker';
import { isSketchEntityReferencePreview } from '../../reference/sketch-entity-reference/command/preview/SketchEntityReferencePreviewMarker';
import { findAncestorSketchGroupId } from '../SketchEditingInteractionGuard';
import type { Viewer } from '../../viewer/Viewer';
import { ViewerMode } from '../../viewer/ViewerMode';
import { PointSnapStrategyType } from '../pointSnap';
import type { SketchGeometryRelationLabelManager } from '../SketchGeometryRelationLabelManager';
import { GeometryInteractionState } from './GeometryInteractionState';
import type { SelectionInteractionMeta } from '../../imported-model/render/selection-extension';
import { ImportedModelTreeHoverBoundingBoxRenderer } from '../../imported-model/highlight/ImportedModelTreeHoverBoundingBoxRenderer';

export { GeometryInteractionState } from './GeometryInteractionState';

/** 三维悬浮边颜色。 */
const HOVER_EDGE_COLOR = 0xf48611;
/** 三维选中边颜色。 */
const SELECTED_EDGE_COLOR = 0x53a9ff;
//------ 装配体正式基准对象移入状态 开始------
/** 基准面移入颜色。 */
const DATUM_PLANE_HOVER_COLOR = 0xf48611;
/** 基准面选中颜色。 */
const DATUM_PLANE_SELECTED_COLOR = 0x53a9ff;
/** 基准面移入透明度。 */
const DATUM_PLANE_HOVER_OPACITY = 0.42;
/** 基准面选中透明度。 */
const DATUM_PLANE_SELECTED_OPACITY = 0.58;
/** 基准面边框移入透明度。 */
const DATUM_PLANE_HOVER_EDGE_OPACITY = 0.95;
/** 基准面边框选中透明度。 */
const DATUM_PLANE_SELECTED_EDGE_OPACITY = 1;
//------ 装配体正式基准对象移入状态 结束------

/** 交互模式上下文。 */
export interface InteractionModeContext {
    /** 当前 Viewer 模式。 */
    mode: ViewerMode;
}

/**
 * 材质应用来源枚举。
 */
export enum MaterialApplySource {
    /** 直接触发。 */
    Direct = 'direct',
    /** 线端点联动触发。 */
    LinkedLine = 'linked-line',
}

/**
 * 材质快照。
 */
export interface MaterialSnapshot {
    /** 目标对象。 */
    object: THREE.Mesh | THREE.Line | THREE.Sprite;
    /** 原始材质。 */
    material: THREE.Material | THREE.Material[];
}

/**
 * 外部手动状态提示记录。
 */
export interface ManualStateHintRecord {
    /** 目标对象。 */
    object: THREE.Mesh | THREE.Line | THREE.Sprite;
    /** 目标状态。 */
    state: GeometryInteractionState;
}

/**
 * 设计树特征节点的视觉作用域。
 *
 * 设计说明：
 * 1. 合并实体中一个结果体可能承载多个历史特征，特征树节点选择不能再等同于实体选择；
 * 2. 该结构只表达“指定 featureId 在这些结果体上的贡献范围”，不参与真实拾取或命令选择；
 * 3. resultMeshes 由 Viewer 根据稳定 objectId 解析，状态类只消费已经确认存在的运行时对象。
 */
export interface FeatureVisualScope {
    /** 被设计树节点指向的特征 ID。 */
    featureId: string;
    /** 该特征当前落在的结果体列表。 */
    resultMeshes: FeatureResultMesh[];
}

/**
 * 交互视觉状态运行时数据。
 */
export interface InteractionVisualStateRuntime {
    /** 每个对象的当前状态。 */
    objectStateMachine: Map<string, GeometryInteractionState>;
    /** 外部状态提示。 */
    externalStateHints: Map<string, GeometryInteractionState>;
    /** 外部手动状态提示（仅允许显式 restore 时移除）。 */
    manualStateHints: Map<string, ManualStateHintRecord>;
    /** 外部手动可见性提示（用于点捕捉等临时预览可见对象）。 */
    manualVisibilityHints: Set<string>;
    /** 原始材质快照集合。 */
    originalMaterialSnapshots: Map<string, MaterialSnapshot>;
    /** 线状态材质缓存（按基础线型 + 交互状态组合键缓存）。 */
    stateLineMaterialCache: Map<string, SketchLineMaterial>;
    /** 面状态材质缓存。 */
    stateMeshMaterialCache: Map<
        string,
        THREE.MeshBasicMaterial | THREE.MeshStandardMaterial | THREE.MeshPhongMaterial
    >;
    /** 控制点状态材质缓存。 */
    stateControlPointMaterialCache: Map<GeometryInteractionState, THREE.SpriteNodeMaterial>;
}

/**
 * 交互视觉状态刷新上下文。
 */
export interface InteractionVisualStateContext {
    /** 当前模式上下文。 */
    modeContext: InteractionModeContext;
    /** 当前悬浮对象。 */
    hoveredObject: THREE.Object3D | null;
    /** 当前悬浮对象对应的交互语义元信息。 */
    hoveredInteractionMeta?: SelectionInteractionMeta;
    //------ 线性零部件阵列节点包围盒移入 开始------
    /** 当前程序化多目标移入对象列表。 */
    programmaticHoveredObjects: THREE.Object3D[];
    /** 当前程序化多目标移入对应的交互语义元信息。 */
    programmaticHoveredInteractionMeta?: SelectionInteractionMeta;
    //------ 线性零部件阵列节点包围盒移入 结束------
    //------ 设计树程序化特征移入高亮 开始------
    /** 当前程序化移入命中的特征结果体。 */
    programmaticHoveredFeatureResultMesh: FeatureResultMesh | null;
    /** 当前设计树特征节点 hover 对应的局部视觉范围。 */
    programmaticHoveredFeatureVisualScope: FeatureVisualScope | null;
    //------ 设计树程序化特征移入高亮 结束------
    /** 当前选中对象列表。 */
    selectedObjects: THREE.Object3D[];
    /** 当前主选对象对应的交互语义元信息。 */
    selectedInteractionMeta?: SelectionInteractionMeta;
    /** 当前设计树特征节点选中对应的局部视觉范围。 */
    selectedFeatureVisualScope: FeatureVisualScope | null;
    /**
     * 当前命令态接管的拓扑代理对象。
     *
     * 设计说明：
     * Mate PropertyManager 会把 FeatureFace / FeatureLine / FeaturePoint 放入独立命令态
     * overlay 通道。普通视觉状态必须知道这些对象已被命令接管，否则会再次把最后点击的
     * 代理写成 selected 材质，形成“高亮块停在旧位置、真实实体已被预览移动”的错位。
     */
    commandSelectedTopologyProxyObjects?: THREE.Object3D[];
    /** 约束标签状态机。 */
    relationLabelState: SketchGeometryRelationLabelManager;
    /** 已同步标签的对象 ID 集合。 */
    syncedMarkerObjectIdSet: Set<string>;
    /** 测量选中视觉管辖对象 ID 集合（底层状态机应跳过覆写）。 */
    measureSelectedObjectIds: ReadonlySet<string>;
}

/**
 * 交互视觉状态抽象类。
 * 说明：
 * 1. 该类只约束“如何修改状态”的能力；
 * 2. 管理器负责模式切换、事件订阅与状态类切换；
 * 3. 具体状态实现必须显式抛错或返回确定结果，禁止写兜底逻辑。
 */
export abstract class AbstractInteractionVisualState {
    /** 状态运行时。 */
    protected readonly runtime: InteractionVisualStateRuntime;
    /** Viewer 实例引用，用于查询点捕捉策略启用状态。 */
    protected readonly viewer: Viewer;
    /** imported-model 多目标程序化 hover 包围盒渲染器。 */
    private readonly importedModelTreeHoverBoundingBoxRenderer: ImportedModelTreeHoverBoundingBoxRenderer;
    /** 草图状态橙色。 */
    private static readonly SKETCH_ACTIVE_ORANGE = new THREE.Color(0xffa500);
    /** 状态机转移表。 */
    private readonly stateTransitions: Readonly<Record<GeometryInteractionState, ReadonlySet<GeometryInteractionState>>> = {
        [GeometryInteractionState.Default]: new Set([
            GeometryInteractionState.Default,
            GeometryInteractionState.Hover,
            GeometryInteractionState.Selected,
            GeometryInteractionState.Creating,
        ]),
        [GeometryInteractionState.Hover]: new Set([
            GeometryInteractionState.Default,
            GeometryInteractionState.Hover,
            GeometryInteractionState.Selected,
        ]),
        [GeometryInteractionState.Selected]: new Set([
            GeometryInteractionState.Default,
            GeometryInteractionState.Hover,
            GeometryInteractionState.Selected,
        ]),
        [GeometryInteractionState.Creating]: new Set([
            GeometryInteractionState.Default,
            GeometryInteractionState.Hover,
            GeometryInteractionState.Selected,
            GeometryInteractionState.Creating,
        ]),
    };

    /**
     * 构造函数。
     * @param runtime 状态运行时。
     * @param viewer Viewer 实例。
     */
    protected constructor(runtime: InteractionVisualStateRuntime, viewer: Viewer) {
        this.runtime = runtime;
        this.viewer = viewer;
        this.importedModelTreeHoverBoundingBoxRenderer = new ImportedModelTreeHoverBoundingBoxRenderer({
            scene: viewer.scene,
            sceneHelpers: viewer.sceneHelpers,
            findSceneObjectById: (objectId: string) => viewer.findSceneObjectById(objectId),
        });
    }

    /**
     * 刷新视觉状态。
     * @param context 当前刷新上下文。
     */
    public refreshVisualState(context: InteractionVisualStateContext): void {
        this.restoreAllStyledMaterials(context);
        this.runtime.objectStateMachine.clear();
        this.applyDefaultMaterialsToSketchObjects(context);

        context.relationLabelState.clearMarkerVisualStates();

        this.runtime.externalStateHints.forEach((hintState, objectUuid) => {
            const materialSnapshot = this.runtime.originalMaterialSnapshots.get(objectUuid);
            if (!materialSnapshot) {
                return;
            }

            this.setObjectState(
                materialSnapshot.object,
                hintState,
                MaterialApplySource.Direct,
                context,
            );
        });

        // 应用点捕捉等非选择事件写入的临时交互状态。
        this.applyManualStateHints(context);

        const selectedObjectUuidSet = new Set<string>();
        context.selectedObjects.forEach((object) => {
            selectedObjectUuidSet.add(object.uuid);
            this.setObjectState(
                object,
                GeometryInteractionState.Selected,
                MaterialApplySource.Direct,
                context,
            );
        });

        this.applySelectedLineControlPointState(context, selectedObjectUuidSet);

        if (
            context.hoveredObject
            && !selectedObjectUuidSet.has(context.hoveredObject.uuid)
            && !this.isMeasureSelectionManagedObject(context, context.hoveredObject)
        ) {
            const allowHover = this.canHoverPointInDrawingMode(context.hoveredObject, context);

            if (allowHover) {
                if (!isSketchContour(context.hoveredObject)) {
                    this.setObjectState(
                        context.hoveredObject,
                        GeometryInteractionState.Hover,
                        MaterialApplySource.Direct,
                        context,
                    );
                }

                this.applyLineControlPointHoverState(context, context.hoveredObject, selectedObjectUuidSet);
                this.highlightContourLinkedEdgesOnHover(context, context.hoveredObject);
            }
        }

        this.applyFeatureFaceHoverBoundaryLines(context);

        this.syncSelectedObjectConstraintMarkers(context);
    }

    /**
     * 应用外部手动交互状态提示。
     *
     * `InteractionStateManager.setSketchObjectState()` 只负责维护提示记录；所有草图视觉模式
     * 都必须在刷新骨架中消费该记录，才能让拖动捕捉、快速捕捉等被普通 hover 事件短路的
     * 场景真正显示临时高亮。后续真实 Selected / Hover 状态仍按状态优先级继续叠加。
     *
     * @param context 当前刷新上下文。
     */
    protected applyManualStateHints(context: InteractionVisualStateContext): void {
        this.runtime.manualStateHints.forEach((hintRecord) => {
            this.snapshotOriginalMaterial(hintRecord.object);
            this.setObjectState(
                hintRecord.object,
                hintRecord.state,
                MaterialApplySource.Direct,
                context,
            );
        });
    }

    /**
     * 同步外部主动修改后的对象材质快照。
     * @param object 外部已更新材质的对象。
     * @param context 当前刷新上下文。
     */
    public syncExternalMaterialSnapshot(object: THREE.Object3D, context: InteractionVisualStateContext): void {
        if (!this.isStateManagedObject(object)) {
            return;
        }

        this.snapshotOriginalMaterial(object);
        const snapshot = this.runtime.originalMaterialSnapshots.get(object.uuid);
        if (!snapshot) {
            throw new Error(`未找到对象材质快照：${object.uuid}`);
        }

        snapshot.material = object.material;

        const hintState = this.resolveExternalStateHintByMaterial(object, object.material);
        if (hintState === GeometryInteractionState.Default) {
            this.runtime.externalStateHints.delete(object.uuid);
        } else {
            this.runtime.externalStateHints.set(object.uuid, hintState);
        }

        void context;
    }

    /**
     * 同步指定对象的材质快照。
     * @param object 目标对象。
     * @param material 指定材质。
     * @param context 当前刷新上下文。
     */
    public syncMaterialSnapshot(
        object: THREE.Object3D,
        material: THREE.Material,
        context: InteractionVisualStateContext,
    ): void {
        if (!this.isStateManagedObject(object)) {
            return;
        }

        this.snapshotOriginalMaterial(object);
        const snapshot = this.runtime.originalMaterialSnapshots.get(object.uuid);
        if (!snapshot) {
            throw new Error(`未找到对象材质快照：${object.uuid}`);
        }

        snapshot.material = material;

        const hintState = this.resolveExternalStateHintByMaterial(object, material);
        if (hintState === GeometryInteractionState.Default) {
            this.runtime.externalStateHints.delete(object.uuid);
        } else {
            this.runtime.externalStateHints.set(object.uuid, hintState);
        }

        void context;
    }

    /**
     * 记录对象原始材质。
     * @param object 目标对象。
     */
    public snapshotOriginalMaterial(object: THREE.Mesh | THREE.Line | THREE.Sprite): void {
        if (this.runtime.originalMaterialSnapshots.has(object.uuid)) {
            return;
        }

        this.runtime.originalMaterialSnapshots.set(object.uuid, {
            object,
            material: object.material,
        });
    }

    /**
     * 根据 objectId 查询原始材质快照。
     * @param objectId 对象唯一标识（即 THREE.Object3D.uuid）。
     * @returns 对应的材质快照；不存在时返回 null。
     */
    public getOriginalMaterialSnapshotByObjectId(objectId: string): MaterialSnapshot | null {
        return this.runtime.originalMaterialSnapshots.get(objectId) ?? null;
    }

    /**
     * 重置当前视觉状态。
     */
    public resetVisualState(): void {
        this.restoreAllStyledMaterials();
        this.runtime.objectStateMachine.clear();
        this.runtime.externalStateHints.clear();
        this.runtime.manualVisibilityHints.clear();
        this.importedModelTreeHoverBoundingBoxRenderer.clear();
    }

    /**
     * 清空材质缓存。
     */
    public clearStateMaterialCache(): void {
        this.runtime.stateLineMaterialCache.clear();
        this.runtime.stateMeshMaterialCache.clear();
        this.runtime.stateControlPointMaterialCache.clear();
        LineMaterialFactory.clearCache();
    }

    /**
     * 释放资源。
     */
    public dispose(): void {
        this.resetVisualState();
        this.importedModelTreeHoverBoundingBoxRenderer.dispose();
        this.clearStateMaterialCache();
    }

    /**
     * 应用 imported-model 多目标程序化 hover 包围盒。
     *
     * 设计说明：
     * 1. 单对象 imported-model 树 hover 已由 ImportedModelHighlightController 统一接管；
     * 2. 这里只处理线性零部件阵列等一次对应多个运行时对象的程序化 hover；
     * 3. renderer 内部只接受 `source === 'feature-tree'`，因此不会改变图形区鼠标 hover 点/线/面行为。
     *
     * @param context 当前视觉状态上下文。
     */
    protected applyImportedModelTreeHoverBoundingBoxes(context: InteractionVisualStateContext): void {
        this.importedModelTreeHoverBoundingBoxRenderer.apply(context);
    }

    /**
     * 判断绘制模式下是否允许对指定点对象应用 hover 高亮。
     * @param hoveredObject 当前悬浮对象。
     * @param context 当前刷新上下文。
     * @returns 是否允许 hover 高亮。
     */
    protected canHoverPointInDrawingMode(hoveredObject: THREE.Object3D, context: InteractionVisualStateContext): boolean {
        if (context.modeContext.mode !== ViewerMode.SketchDrawing) {
            return true;
        }

        if (!isSketchPointObject(hoveredObject)) {
            return true;
        }

        const pointObj = hoveredObject as SketchPointObject;
        if (pointObj.isEndPoint || pointObj.pointKind === SketchPointKind.SketchPoint) {
            const snapManager = this.viewer.pointSnapManager;
            if (!snapManager || !snapManager.isSnapEnabled) {
                return false;
            }
            return snapManager.isStrategyEnabled(PointSnapStrategyType.EndpointOrigin);
        }

        void context;
        return true;
    }

    /**
     * 对选中线类对象执行控制点联动高亮。
     * @param context 当前刷新上下文。
     * @param selectedObjectUuidSet 当前选中对象 UUID 集合。
     */
    protected applySelectedLineControlPointState(
        context: InteractionVisualStateContext,
        selectedObjectUuidSet: Set<string>,
    ): void {
        context.selectedObjects.forEach((selectedObject) => {
            this.applyLineControlPointVisualState(
                context,
                selectedObject,
                selectedObjectUuidSet,
                GeometryInteractionState.Selected,
            );
        });
    }

    /**
     * 对悬浮线类对象执行控制点联动高亮。
     * @param context 当前刷新上下文。
     * @param hoveredObject 当前悬浮对象。
     * @param selectedObjectUuidSet 当前选中对象 UUID 集合。
     */
    protected applyLineControlPointHoverState(
        context: InteractionVisualStateContext,
        hoveredObject: THREE.Object3D,
        selectedObjectUuidSet: Set<string>,
    ): void {
        void context;
        void hoveredObject;
        void selectedObjectUuidSet;
    }

    /**
     * 当移入草图轮廓时，将构成该轮廓的边直接高亮为悬浮态。
     * @param context 当前刷新上下文。
     * @param hoveredObject 当前移入对象。
     */
    protected highlightContourLinkedEdgesOnHover(
        context: InteractionVisualStateContext,
        hoveredObject: THREE.Object3D,
    ): void {
        if (!isSketchContour(hoveredObject)) {
            return;
        }
        hoveredObject.getLinkObjects().forEach((edge) => {
            this.setObjectState(edge, GeometryInteractionState.Hover, MaterialApplySource.Direct, context);
        });
    }

    //------ 装配体正式基准对象移入状态 开始------
    /**
     * 应用正式基准对象交互状态。
     *
     * 设计说明：
     * 1. 正式基准对象视觉语义需要同时服务 Default 与 Assembly；
     * 2. 这里只处理 datum plane / axis / point，不附带任何默认模式其它特征高亮分支；
     * 3. 调用方负责先完成自身基础状态机刷新，本方法只补正式基准对象 hover / selected 材质。
     *
     * @param context 当前刷新上下文。
     */
    protected applyDatumReferenceInteractionStates(context: InteractionVisualStateContext): void {
        const selectedObjectIdSet = new Set(context.selectedObjects.map((object) => object.uuid));

        // 基准面以 group 作为选择语义对象，但交互态只高亮对应边线。
        this.viewer.getAllDatumPlanes()
            .filter((datumPlane) => datumPlane.isVisible())
            .forEach((datumPlane) => {
                const planeObject = datumPlane.getObject3D();
                if (selectedObjectIdSet.has(planeObject.uuid)) {
                    this.applyDatumPlaneState(planeObject, GeometryInteractionState.Selected, context);
                    return;
                }
                if (context.hoveredObject === planeObject) {
                    this.applyDatumPlaneState(planeObject, GeometryInteractionState.Hover, context);
                }
            });

        // 基准轴沿用线材质工厂，保持与特征线 hover / selected 的颜色体系一致。
        this.viewer.datumAxisManager?.getAllAxes()
            .filter((datumAxis) => datumAxis.isVisible())
            .forEach((datumAxis) => {
                if (selectedObjectIdSet.has(datumAxis.uuid)) {
                    this.applyDatumAxisState(datumAxis, GeometryInteractionState.Selected, context);
                    return;
                }
                if (context.hoveredObject === datumAxis) {
                    this.applyDatumAxisState(datumAxis, GeometryInteractionState.Hover, context);
                }
            });

        // 基准点默认可能用透明显示策略，进入交互态时需要显式保证可见。
        this.viewer.datumPointManager?.getAllPoints()
            .filter((datumPoint) => datumPoint.isVisible())
            .forEach((datumPoint) => {
                if (selectedObjectIdSet.has(datumPoint.uuid)) {
                    this.applyDatumPointState(datumPoint, GeometryInteractionState.Selected, context);
                    return;
                }
                if (context.hoveredObject === datumPoint) {
                    this.applyDatumPointState(datumPoint, GeometryInteractionState.Hover, context);
                }
            });
    }

    /**
     * 应用基准面交互状态。
     *
     * @param planeObject 基准面根对象。
     * @param state 目标状态。
     */
    private applyDatumPlaneState(
        planeObject: THREE.Object3D,
        state: GeometryInteractionState.Hover | GeometryInteractionState.Selected,
        context: InteractionVisualStateContext,
    ): void {
        if (this.isMeasureSelectionManagedObject(context, planeObject)) {
            return;
        }
        const edgeObjects: THREE.Line[] = [];
        planeObject.traverse((childObject) => {
            if (childObject instanceof THREE.Line) {
                edgeObjects.push(childObject);
            }
        });
        if (edgeObjects.length === 0) {
            return;
        }

        // 基准面自身不再整片高亮，只高亮由 EdgesGeometry 创建的边框子对象。
        const edgeMaterialCacheKey = `datum-plane:${state}:edge`;
        const cachedEdgeMaterial = this.runtime.stateLineMaterialCache.get(edgeMaterialCacheKey);
        const edgeMaterial = cachedEdgeMaterial ?? new THREE.LineBasicMaterial({
            color: state === GeometryInteractionState.Selected ? DATUM_PLANE_SELECTED_COLOR : DATUM_PLANE_HOVER_COLOR,
            transparent: true,
            opacity: state === GeometryInteractionState.Selected ? DATUM_PLANE_SELECTED_EDGE_OPACITY : DATUM_PLANE_HOVER_EDGE_OPACITY,
            depthWrite: false,
        });
        if (!cachedEdgeMaterial) {
            this.runtime.stateLineMaterialCache.set(edgeMaterialCacheKey, edgeMaterial);
        }

        edgeObjects.forEach((edgeObject) => {
            if (this.isMeasureSelectionManagedObject(context, edgeObject)) {
                return;
            }
            this.snapshotOriginalMaterial(edgeObject);
            edgeObject.material = edgeMaterial;
            this.runtime.objectStateMachine.set(edgeObject.uuid, state);
        });
    }

    /**
     * 应用基准轴交互状态。
     *
     * @param axisObject 基准轴对象。
     * @param state 目标状态。
     * @param context 当前刷新上下文。
     */
    private applyDatumAxisState(
        axisObject: THREE.Line,
        state: GeometryInteractionState.Hover | GeometryInteractionState.Selected,
        context: InteractionVisualStateContext,
    ): void {
        if (this.isMeasureSelectionManagedObject(context, axisObject)) {
            return;
        }
        this.snapshotOriginalMaterial(axisObject);
        //------ 基准轴点画线材质 开始------
        // 正式基准轴在交互态也必须保持点画线，只允许颜色随选中 / 移入语义变化。
        axisObject.material = this.createDatumAxisInteractionMaterial(axisObject, state);
        if (isDashDotLIneNodeMaterial(axisObject.material)) {
            syncThinChainLineGeometry(axisObject);
        } else if (axisObject.material instanceof THREE.LineDashedMaterial) {
            axisObject.computeLineDistances();
        }
        //------ 基准轴点画线材质 结束------
        this.runtime.objectStateMachine.set(axisObject.uuid, state);
    }

    /**
     * 应用基准点交互状态。
     *
     * @param pointObject 基准点对象。
     * @param state 目标状态。
     */
    private applyDatumPointState(
        pointObject: THREE.Sprite,
        state: GeometryInteractionState.Hover | GeometryInteractionState.Selected,
        context: InteractionVisualStateContext,
    ): void {
        if (this.isMeasureSelectionManagedObject(context, pointObject)) {
            return;
        }
        this.snapshotOriginalMaterial(pointObject);
        // 正式基准点使用专用点材质，避免被草图点或特征点状态误复用。
        pointObject.material = state === GeometryInteractionState.Selected
            ? PointsMaterialFactory.getDatumPointSelectedSpriteMaterial()
            : PointsMaterialFactory.getDatumPointHoverSpriteMaterial();
        pointObject.visible = true;
        this.runtime.objectStateMachine.set(pointObject.uuid, state);
    }

    /**
     * 创建或读取正式基准轴交互态材质。
     *
     * 设计说明：
     * 1. 点画线材质需要保存本线段点列，因此不能复用普通线的全局共享材质；
     * 2. 交互状态刷新会频繁执行，按“轴对象 + 状态”缓存可以避免每帧创建 NodeMaterial；
     * 3. 具体点列仍在赋值后由 `syncThinChainLineGeometry()` 写入，确保移动重算后材质输入同步。
     *
     * @param axisObject 基准轴对象。
     * @param state 目标交互状态。
     * @returns 基准轴交互态点画线材质。
     */
    private createDatumAxisInteractionMaterial(
        axisObject: THREE.Line,
        state: GeometryInteractionState.Hover | GeometryInteractionState.Selected,
    ): SketchLineMaterial {
        const materialState = state === GeometryInteractionState.Selected
            ? LineMaterialState.Selected
            : LineMaterialState.Hover;
        const cacheKey = `datum-axis:${axisObject.uuid}:${materialState}`;
        const cachedMaterial = this.runtime.stateLineMaterialCache.get(cacheKey);
        if (cachedMaterial) {
            return cachedMaterial;
        }

        const createdMaterial = LineMaterialFactory.getLineMaterialByBaseStyle(
            SketchEdgeBaseStyle.Construction,
            materialState,
        );
        this.runtime.stateLineMaterialCache.set(cacheKey, createdMaterial);
        return createdMaterial;
    }
    //------ 装配体正式基准对象移入状态 结束------

    /**
     * 恢复所有接管材质。
     */
    protected restoreAllStyledMaterials(context?: InteractionVisualStateContext): void {
        context?.measureSelectedObjectIds.forEach((objectId) => {
            this.runtime.originalMaterialSnapshots.delete(objectId);
            this.runtime.objectStateMachine.delete(objectId);
        });

        this.runtime.originalMaterialSnapshots.forEach((snapshot) => {
            if (this.viewer.isSketchEditingVisualPreviewObject?.(snapshot.object) === true) {
                // 预览对象已由当前命令接管材质，不得用旧交互快照覆盖最新预览状态。
                return;
            }
            snapshot.object.material = this.resolveRestoredMaterial(snapshot.object, snapshot.material);
            if (isSketchPointObject(snapshot.object)) {
                snapshot.object.restoreDefaultScale();
            }
        });
        this.runtime.originalMaterialSnapshots.clear();
    }

    /**
     * 为对象设置目标状态。
     * @param object 目标对象。
     * @param targetState 目标状态。
     * @param source 材质应用来源。
     * @param context 当前刷新上下文。
     */
    protected setObjectState(
        object: THREE.Object3D,
        targetState: GeometryInteractionState,
        source: MaterialApplySource,
        context: InteractionVisualStateContext,
    ): void {
        if (this.isMeasureSelectionManagedObject(context, object)) {
            return;
        }

        if (object instanceof THREE.Group) {
            object.traverse((child) => {
                if (!this.shouldApplyObjectStateToGroupChild(object, child, targetState, source, context)) {
                    return;
                }
                if (this.isStateManagedObject(child)) {
                    this.setObjectState(child, targetState, source, context);
                }
            });
            return;
        }

        if (!this.isStateManagedObject(object)) {
            return;
        }
        if (!this.canApplyToObject(object, context)) {
            return;
        }

        const currentState = this.runtime.objectStateMachine.get(object.uuid) ?? GeometryInteractionState.Default;
        if (!this.canTransitState(currentState, targetState)) {
            return;
        }

        this.applyMaterialState(object, targetState, source, context);
        this.runtime.objectStateMachine.set(object.uuid, targetState);
    }

    /**
     * 判断 Group 状态递归时是否允许处理当前子对象。
     *
     * 设计说明：
     * 1. Group 本身常用于表达复合草图实体，默认递归全部可管理子对象；
     * 2. 具体视觉状态可覆写该钩子，排除不应被整体选中点亮的派生辅助点；
     * 3. 过滤发生在状态递归入口，不改变点对象自身的直接 hover / selected 语义。
     *
     * @param groupObject 当前正在递归应用状态的父级 Group。
     * @param childObject 当前待处理的子对象。
     * @param targetState 本次应用的目标交互状态。
     * @param source 本次状态应用来源。
     * @param context 当前刷新上下文。
     * @returns 允许继续处理该子对象时返回 true。
     */
    protected shouldApplyObjectStateToGroupChild(
        groupObject: THREE.Group,
        childObject: THREE.Object3D,
        targetState: GeometryInteractionState,
        source: MaterialApplySource,
        context: InteractionVisualStateContext,
    ): boolean {
        void groupObject;
        void childObject;
        void targetState;
        void source;
        void context;
        return true;
    }

    /**
     * 同步选中对象的约束标识显示。
     * @param context 当前刷新上下文。
     */
    protected syncSelectedObjectConstraintMarkers(context: InteractionVisualStateContext): void {
        // 绘制工具完成实体后仍处于 SketchDrawing；它与普通草图编辑共享关系标签展示语义。
        const shouldDisplayRelationMarkers = context.modeContext.mode === ViewerMode.Sketch
            || context.modeContext.mode === ViewerMode.SketchDrawing;
        if (!shouldDisplayRelationMarkers) {
            context.relationLabelState.clearAllMarkers();
            context.relationLabelState.clearMarkerVisualStates();
            context.syncedMarkerObjectIdSet.clear();
            return;
        }

        if (context.selectedObjects.length <= 0) {
            context.relationLabelState.clearAllMarkers();
            context.relationLabelState.clearMarkerVisualStates();
            context.syncedMarkerObjectIdSet.clear();
            return;
        }

        const currentMarkerObjectIdSet = new Set<string>();
        const clickSelectedMarkerKeySet = new Set<string>();

        context.selectedObjects.forEach((selectedObject) => {
            const syncedObjectIds = context.relationLabelState.syncObjectMarkers(selectedObject, true);
            syncedObjectIds.forEach((syncedObjectId) => {
                currentMarkerObjectIdSet.add(syncedObjectId);
                context.relationLabelState.getObjectMarkerKeys(syncedObjectId).forEach((key) => {
                    clickSelectedMarkerKeySet.add(key);
                });
            });
        });

        context.syncedMarkerObjectIdSet.forEach((objectId) => {
            if (currentMarkerObjectIdSet.has(objectId)) {
                return;
            }
            context.relationLabelState.clearObjectMarkers(objectId);
        });

        context.syncedMarkerObjectIdSet.clear();
        currentMarkerObjectIdSet.forEach((objectId) => {
            context.syncedMarkerObjectIdSet.add(objectId);
        });

        context.relationLabelState.syncMarkerStateMachine({
            clickSelectedMarkerKeys: Array.from(clickSelectedMarkerKeySet.values()),
            hoveredMarkerKeys: [],
            menuSelectedMarkerKeys: [],
        });
    }

    /**
     * 面移入时为当前面的边界线显示黑色轮廓。
     *
     * 设计说明：
     * 1. 默认浏览态特征边线已隐藏，仅在鼠标移入某个 `FeatureFace` 时恢复该面的边界线；
     * 2. 边界线使用黑色而非法线 Hover 橙色，避免与棱边移入语义混淆；
     * 3. 只处理 `hoveredObject` 为 `FeatureFace` 的路径，不联动整颗结果体的全部边线。
     *
     * @param context 当前刷新上下文。
     */
    protected applyFeatureFaceHoverBoundaryLines(context: InteractionVisualStateContext): void {
        const hoveredObject = context.hoveredObject;
        if (!hoveredObject || !isFeatureFace(hoveredObject)) {
            return;
        }
        if (context.selectedObjects.some((selectedObject) => selectedObject.uuid === hoveredObject.uuid)) {
            return;
        }

        const boundaryMaterial = LineMaterialFactory.getFeatureFaceHoverBoundaryLineMaterial();
        hoveredObject.getBoundaryLines().forEach((boundaryLine) => {
            this.snapshotOriginalMaterial(boundaryLine);
            if (boundaryLine.material !== boundaryMaterial) {
                disposeOwnedMaterial(boundaryLine.material);
                boundaryLine.material = boundaryMaterial;
            }
        });
    }

    /**
     * 对指定线类对象应用控制点联动状态。
     * @param context 当前刷新上下文。
     * @param targetObject 目标对象。
     * @param selectedObjectUuidSet 当前选中对象 UUID 集合。
     * @param state 目标状态。
     */
    private applyLineControlPointVisualState(
        context: InteractionVisualStateContext,
        targetObject: THREE.Object3D,
        selectedObjectUuidSet: Set<string>,
        state: GeometryInteractionState,
    ): void {
        if (
            !isSketchLineObject(targetObject)
            && !isSketchSplineObject(targetObject)
            && !isSketchStyleSplineObject(targetObject)
            && !isSketchParabolaObject(targetObject)
        ) {
            return;
        }

        const linkedControlPoints = this.collectLineControlPoints(targetObject, context);
        linkedControlPoints.forEach((controlPoint) => {
            if (selectedObjectUuidSet.has(controlPoint.uuid)) {
                return;
            }
            this.setObjectState(controlPoint, state, MaterialApplySource.LinkedLine, context);
        });
    }

    /**
     * 收集线类对象关联的控制点对象。
     * @param lineObject 线类对象。
     * @param context 当前刷新上下文。
     * @returns 去重后的控制点列表。
     */
    private collectLineControlPoints(
        lineObject: SketchLineObject | SketchSplineObject | SketchStyleSplineObject | SketchParabolaObject,
        context: InteractionVisualStateContext,
    ): SketchPointObject[] {
        const pointsById = new Map<string, SketchPointObject>();

        const tryAddControlPoint = (candidate: THREE.Object3D | null | undefined): void => {
            if (!candidate || !isSketchPointObject(candidate)) {
                return;
            }
            if (!this.canApplyToObject(candidate, context)) {
                return;
            }
            pointsById.set(candidate.uuid, candidate);
        };

        if (isSketchSplineObject(lineObject) || isSketchStyleSplineObject(lineObject)) {
            lineObject.getControlPoints().forEach((pointObject) => {
                tryAddControlPoint(pointObject);
            });
            return Array.from(pointsById.values());
        }

        //------ 直线默认中点高亮收口 开始------
        /**
         * 直线默认中点是派生控制点，点击或悬浮直线时不应被联动点亮。
         * 只有用户直接命中中点或点捕捉需要临时显示时，才由点对象自身进入交互态。
         */
        lineObject.getRelatedSketchPointsSnapshot().forEach((pointObject) => {
            if (pointObject.isMidPoint) {
                return;
            }
            const isControlPointKind = pointObject.pointKind === SketchPointKind.ControlPoint;
            const isLineFeaturePoint = pointObject.isEndPoint;
            if (!isControlPointKind && !isLineFeaturePoint) {
                return;
            }
            tryAddControlPoint(pointObject);
        });
        //------ 直线默认中点高亮收口 结束------

        return Array.from(pointsById.values());
    }

    /**
     * 判断状态机是否允许状态转移。
     * @param fromState 当前状态。
     * @param toState 目标状态。
     * @returns 是否允许。
     */
    private canTransitState(fromState: GeometryInteractionState, toState: GeometryInteractionState): boolean {
        return this.stateTransitions[fromState].has(toState);
    }

    /**
     * 应用材质状态。
     * @param object 目标对象。
     * @param state 状态类型。
     * @param source 材质应用来源。
     * @param context 当前刷新上下文。
     */
    private applyMaterialState(
        object: THREE.Mesh | THREE.Line | THREE.Sprite,
        state: GeometryInteractionState,
        source: MaterialApplySource,
        context: InteractionVisualStateContext,
    ): void {
        this.snapshotOriginalMaterial(object);

        if (state === GeometryInteractionState.Default) {
            this.applyDefaultMaterialState(object, context);
            return;
        }

        if (isFeatureLine(object)) {
            const nextMaterial = this.createFeatureLineMaterial(state);
            // 共享缓存仅作模板，实际绑定必须随短命拓扑线一起释放。
            object.material = object.getInteractionMaterial(this.resolveLineMaterialState(state), nextMaterial);
            return;
        }

        if (
            isSketchLineObject(object)
            || isSketchSplineObject(object)
            || isSketchStyleSplineObject(object)
            || isSketchCircleObject(object)
            || isSketchArcObject(object)
            || isSketchEllipseObject(object)
            || isSketchParabolaObject(object)
        ) {
            const nextMaterial = this.createLineMaterial(object, state, context);
            if (object.material !== nextMaterial) {
                disposeOwnedMaterial(object.material);
                object.material = nextMaterial;
            }
            if (this.shouldRecomputeLineDistances(object.material)) {
                object.computeLineDistances();
            }
            return;
        }

        if (isSketchPointObject(object)) {
            // 默认态刷新会先按拓扑角色把中点/象限点恢复为隐藏。
            // 当这些点随后进入 Hover / Selected 时，必须显式重新显示，
            // 否则会出现“命中点对象本体但视觉上仍然消失”的问题。
            object.visible = true;
            object.material = this.createPointsNodeMaterial(object, state, source);
            this.applyPointInteractionScale(object, state, source);
            return;
        }

        if (isFeaturePoint(object)) {
            object.material = this.createPointsNodeMaterial(object, state, source);
            object.visible = true;
            return;
        }

        if (isFeatureFace(object)) {
            object.material = this.createFeatureFaceMaterial(object, state);
            return;
        }
    }

    /**
     * 按交互状态同步点对象缩放。
     * @param pointObject 点对象。
     * @param state 当前交互状态。
     * @param source 材质应用来源。
     */
    private applyPointInteractionScale(
        pointObject: SketchPointObject,
        state: GeometryInteractionState,
        source: MaterialApplySource,
    ): void {
        const isDirectHover = state === GeometryInteractionState.Hover
            && source === MaterialApplySource.Direct;
        const isSelected = state === GeometryInteractionState.Selected;
        const shouldApplyInteractionScale = isDirectHover || isSelected;
        if (shouldApplyInteractionScale) {
            pointObject.applyHoverOrSelectedScale();
            return;
        }

        pointObject.restoreDefaultScale();
    }

    /**
     * 对场景中的草图对象统一应用默认态材质。
     */
    protected applyDefaultMaterialsToSketchObjects(context: InteractionVisualStateContext): void {
        this.viewer.scene.traverse((object) => {
            if (!this.shouldApplyDefaultMaterialState(object, context)) {
                return;
            }

            const defaultMaterialTarget = object as THREE.Mesh | THREE.Line | THREE.Sprite;
            this.applyDefaultMaterialState(defaultMaterialTarget, context);
        });
    }

    /**
     * 判断当前刷新是否应对指定对象接管默认态材质。
     *
     * 设计说明：
     * 1. 默认实现保持既有行为，只对状态机允许处理的对象接管默认材质；
     * 2. 子类可以在不改动通用刷新骨架的前提下，排除特定模式下不应被默认态接管的对象；
     * 3. 这样能把“模式差异”收口在状态类自身，避免把 imported-model 等特殊规则散落到通用状态机里。
     *
     * @param object 待判断对象。
     * @param context 当前刷新上下文。
     * @returns 当前模式下允许接管默认态材质时返回 `true`。
     */
    protected shouldApplyDefaultMaterialState(
        object: THREE.Object3D,
        context: InteractionVisualStateContext,
    ): boolean {
        if (
            this.viewer.isSketchEditingVisualPreviewObject?.(object) === true
            && !isSketchEntityReferencePreview(object)
        ) {
            // 通用命令预览的黄色候选由各控制器管理；Convert Entities 预览则必须进入本状态机，统一应用专用蓝色材质。
            return false;
        }
        if (this.isMeasureSelectionManagedObject(context, object)) {
            return false;
        }
        return this.isStateManagedObject(object);
    }

    /**
     * 判断对象是否由测量选中叠加层独占管辖。
     *
     * @param context 当前刷新上下文。
     * @param object 待判断对象。
     * @returns 测量选中管辖对象时返回 true。
     */
    protected isMeasureSelectionManagedObject(
        context: InteractionVisualStateContext,
        object: THREE.Object3D,
    ): boolean {
        return context.measureSelectedObjectIds.has(object.uuid);
    }

    /**
     * 对单个对象应用默认态材质。
     * @param object 目标对象。
     */
    private applyDefaultMaterialState(
        object: THREE.Mesh | THREE.Line | THREE.Sprite,
        context: InteractionVisualStateContext,
    ): void {
        if (isFeatureLine(object)) {
            const nextMaterial = this.createDefaultFeatureLineMaterial(object);
            object.material = object.getInteractionMaterial('default', nextMaterial);
            return;
        }

        if (
            isSketchLineObject(object)
            || isSketchSplineObject(object)
            || isSketchStyleSplineObject(object)
            || isSketchCircleObject(object)
            || isSketchArcObject(object)
            || isSketchEllipseObject(object)
            || isSketchParabolaObject(object)
        ) {
            const nextMaterial = this.createDefaultLineMaterial(object, context);
            if (object.material !== nextMaterial) {
                disposeOwnedMaterial(object.material);
                object.material = nextMaterial;
            }
            if (this.shouldRecomputeLineDistances(object.material)) {
                object.computeLineDistances();
            }
            return;
        }

        if (isSketchPointObject(object)) {
            const shouldKeepVisible = this.runtime.manualVisibilityHints.has(object.uuid);
            object.refreshDefaultVisibilityByTopologyRole();
            if (shouldKeepVisible) {
                object.visible = true;
            }
            object.material = this.createDefaultPointMaterial(object, context);
            if (this.shouldUseReferencePreviewDefaultMaterial(object)) {
                object.applyHoverOrSelectedScale();
                return;
            }
            object.restoreDefaultScale();
            return;
        }

        if (isFeaturePoint(object)) {
            object.material = this.createDefaultPointMaterial(object, context);
            object.visible = false;
            return;
        }

        if (isFeatureFace(object)) {
            const nextMaterial = this.createFeatureFaceMaterial(
                object,
                GeometryInteractionState.Default,
            );
            if (object.material !== nextMaterial) {
                disposeOwnedMaterial(object.material);
                object.material = nextMaterial;
            }
            return;
        }
    }

    /**
     * 解析恢复材质。
     * @param target 目标对象。
     * @param snapshotMaterial 快照材质。
     * @returns 恢复材质。
     */
    private resolveRestoredMaterial(
        target: THREE.Mesh | THREE.Line | THREE.Sprite,
        snapshotMaterial: THREE.Material | THREE.Material[],
    ): THREE.Material | THREE.Material[] {
        if (!(target instanceof SketchPointObject)) {
            return snapshotMaterial;
        }
        if (target.pointKind !== SketchPointKind.SketchPoint) {
            return snapshotMaterial;
        }
        if (Array.isArray(snapshotMaterial)) {
            return snapshotMaterial;
        }

        const createdMaterial = PointsMaterialFactory.getSketchPointSpriteMaterial();
        if (snapshotMaterial === createdMaterial) {
            return PointsMaterialFactory.getSketchPointDefaultSpriteMaterial();
        }
        return snapshotMaterial;
    }

    /**
     * 创建线材质。
     * @param object 目标线对象。
     * @param state 状态类型。
     * @param context 当前刷新上下文。
     * @returns 线材质。
     */
    private createLineMaterial(
        object: THREE.Line,
        state: GeometryInteractionState,
        context: InteractionVisualStateContext,
    ): SketchLineMaterial {
        if (
            object instanceof AbstractSketchEdgeObject
            && object.referenceDiagnosticState === 'failed'
        ) {
            return LineMaterialFactory.getReferenceFailedMaterial(
                this.resolveLineMaterialState(state),
            );
        }
        if (
            object instanceof AbstractSketchEdgeObject
            && object.referenceDiagnosticState === 'dangling'
        ) {
            return LineMaterialFactory.getReferenceDanglingMaterial(
                this.resolveLineMaterialState(state),
            );
        }
        const baseStyle = this.resolveLineBaseStyle(object);
        const isFixedDefaultLine = this.hasFixedDefaultLineState(object);
        if (baseStyle === SketchEdgeBaseStyle.Construction) {
            const color = this.resolveLineColorByState(state, context);
            const lineMaterialState = this.resolveLineMaterialState(state);
            return LineMaterialFactory.getLineMaterial({
                state: lineMaterialState,
                baseStyle,
                sourceMaterial: object.material,
                color,
                lineWidth: 3,
                isFixed: isFixedDefaultLine,
            });
        }

        const cacheKey = this.buildLineMaterialCacheKey(baseStyle, state, isFixedDefaultLine);
        const cachedMaterial = this.runtime.stateLineMaterialCache.get(cacheKey);
        if (cachedMaterial) {
            return cachedMaterial;
        }

        const color = this.resolveLineColorByState(state, context);
        const lineMaterialState = this.resolveLineMaterialState(state);
        const createdMaterial = LineMaterialFactory.getLineMaterial({
            state: lineMaterialState,
            baseStyle,
            sourceMaterial: object.material,
            color,
            lineWidth: 3,
            isFixed: isFixedDefaultLine,
        });

        this.runtime.stateLineMaterialCache.set(cacheKey, createdMaterial);
        return createdMaterial;
    }

    /**
     * 创建特征边交互材质。
     *
     * @param state 当前交互状态。
     * @returns 特征边材质。
     */
    private createFeatureLineMaterial(state: GeometryInteractionState): THREE.LineBasicMaterial {
        const materialState = this.resolveLineMaterialState(state);
        // 命令重置与普通默认态共用同一模板，避免两个等价模板反复替换默认槽。
        // 工厂通过 version 发布默认透明度变化，拓扑线据此释放旧配方并重建对应槽。
        if (materialState === LineMaterialState.Default) {
            return LineMaterialFactory.getFeatureLineMaterialByState(materialState);
        }
        const cacheKey = `feature-line:${materialState}`;
        const cachedMaterial = this.runtime.stateLineMaterialCache.get(cacheKey);
        if (cachedMaterial instanceof THREE.LineBasicMaterial) {
            return cachedMaterial;
        }

        const material = markSharedInteractionMaterial(
            LineMaterialFactory.getFeatureLineMaterialByState(materialState).clone(),
        );
        material.depthWrite = false;
        // 默认态已在上方返回；这里只处理悬停和选中，统一保持前景覆盖策略。
        material.depthTest = false;
        this.runtime.stateLineMaterialCache.set(cacheKey, material);
        return material;
    }

    /**
     * 创建特征边默认态材质。
     *
     * 设计说明：
     * 1. 模型/特征边线作为 Convert Entities 来源时，真实选中会被命令态清空；
     * 2. 视觉反馈依赖实体引用来源高亮标记，因此默认态必须优先读取该标记；
     * 3. FeatureLine 没有草图构造线语义，引用高亮统一按实线基础样式渲染。
     *
     * @param object 目标特征边线。
     * @returns 默认态下应使用的特征边材质。
     */
    private createDefaultFeatureLineMaterial(object: FeatureLine): SketchLineMaterial {
        if (this.shouldUseReferencePreviewDefaultMaterial(object)) {
            return LineMaterialFactory.getReferencePreviewMaterial(SketchEdgeBaseStyle.Basic);
        }
        if (this.shouldUseReferenceSourceHighlightDefaultMaterial(object)) {
            return LineMaterialFactory.getReferenceSourceHighlightMaterial(SketchEdgeBaseStyle.Basic);
        }
        return this.createFeatureLineMaterial(GeometryInteractionState.Default);
    }

    /**
     * 创建默认态线材质。
     * @param object 目标线对象。
     * @returns 默认态线材质。
     */
    private createDefaultLineMaterial(
        object: THREE.Line,
        context: InteractionVisualStateContext,
    ): SketchLineMaterial {
        const baseStyle = this.resolveLineBaseStyle(object);
        if (
            object instanceof AbstractSketchEdgeObject
            && object.referenceDiagnosticState === 'failed'
        ) {
            return LineMaterialFactory.getReferenceFailedMaterial(LineMaterialState.Default);
        }
        if (
            object instanceof AbstractSketchEdgeObject
            && object.referenceDiagnosticState === 'dangling'
        ) {
            return LineMaterialFactory.getReferenceDanglingMaterial(LineMaterialState.Default);
        }
        if (this.shouldUseReferencePreviewDefaultMaterial(object)) {
            return LineMaterialFactory.getReferencePreviewMaterial(baseStyle);
        }
        if (this.shouldUseReferenceSourceHighlightDefaultMaterial(object)) {
            return LineMaterialFactory.getReferenceSourceHighlightMaterial(baseStyle);
        }
        if (this.shouldUseInactiveSketchDefaultMaterial(object, context)) {
            return LineMaterialFactory.getInactiveMaterial(
                baseStyle,
                this.hasFixedDefaultLineState(object),
            );
        }
        return LineMaterialFactory.getLineMaterialByBaseStyle(
            baseStyle,
            LineMaterialState.Default,
            this.hasFixedDefaultLineState(object),
        );
    }

    /**
     * 解析对象基础线型状态。
     * @param object 目标对象。
     * @returns 基础线型状态。
     */
    private resolveLineBaseStyle(object: THREE.Line): SketchEdgeBaseStyle {
        if (object instanceof AbstractSketchEdgeObject) {
            return object.baseStyle;
        }

        return LineMaterialFactory.resolveBaseStyleBySourceMaterial(object.material);
    }

    /**
     * 判断当前材质是否需要重算线距离。
     * @param material 待判断材质。
     * @returns 当材质依赖 lineDistance 或 lineDistancePercent 时返回 true。
     */
    private shouldRecomputeLineDistances(
        material: THREE.Material | THREE.Material[],
    ): boolean {
        const targetMaterial = Array.isArray(material)
            ? material[0]
            : material;

        return targetMaterial instanceof THREE.LineDashedMaterial
            || isDashDotLIneNodeMaterial(targetMaterial);
    }

    /**
     * 构建线状态材质缓存键。
     * @param baseStyle 基础线型状态。
     * @param state 交互状态。
     * @returns 缓存键。
     */
    private buildLineMaterialCacheKey(
        baseStyle: SketchEdgeBaseStyle,
        state: GeometryInteractionState,
        isFixed = false,
    ): string {
        return `${baseStyle}:${state}:${isFixed ? 'fixed' : 'normal'}`;
    }

    /**
     * 将交互状态映射为线材质状态。
     * @param state 交互状态。
     * @returns 线材质状态。
     */
    private resolveLineMaterialState(state: GeometryInteractionState): LineMaterialState {
        if (state === GeometryInteractionState.Hover) {
            return LineMaterialState.Hover;
        }
        if (state === GeometryInteractionState.Selected) {
            return LineMaterialState.Selected;
        }
        return LineMaterialState.Default;
    }

    /**
     * 创建点材质。
     * @param object 目标点对象。
     * @param state 状态类型。
     * @param source 材质应用来源。
     * @returns 点材质。
     */
    private createPointsNodeMaterial(
        object: THREE.Sprite,
        state: GeometryInteractionState,
        source: MaterialApplySource,
    ): THREE.SpriteNodeMaterial {
        if (isFeaturePoint(object)) {
            if (state === GeometryInteractionState.Selected) {
                return object.getInteractionMaterial('selected');
            }
            if (state === GeometryInteractionState.Hover || source === MaterialApplySource.LinkedLine) {
                return object.getInteractionMaterial('hover');
            }
            return object.getInteractionMaterial('default');
        }

        if (isDatumPoint(object)) {
            if (state === GeometryInteractionState.Selected) {
                return PointsMaterialFactory.getDatumPointSelectedSpriteMaterial();
            }
            if (state === GeometryInteractionState.Hover || source === MaterialApplySource.LinkedLine) {
                return PointsMaterialFactory.getDatumPointHoverSpriteMaterial();
            }
            return PointsMaterialFactory.getDatumPointDefaultSpriteMaterial();
        }

        if (object instanceof SketchPointObject && object.isCurveCenterControlPoint) {
            if (state === GeometryInteractionState.Selected) {
                return PointsMaterialFactory.getCurveCenterPointSelectedSpriteMaterial();
            }
            if (state === GeometryInteractionState.Hover || source === MaterialApplySource.LinkedLine) {
                return PointsMaterialFactory.getCurveCenterPointHoverSpriteMaterial();
            }
            throw new Error(`曲线中心控制点仅支持移入或选中材质状态：${state}`);
        }

        if (object instanceof SketchPointObject && object.isEllipseAxisControlPoint()) {
            if (state === GeometryInteractionState.Selected) {
                return PointsMaterialFactory.getSketchPointSelectedSpriteMaterial();
            }
            if (state === GeometryInteractionState.Hover || source === MaterialApplySource.LinkedLine) {
                return PointsMaterialFactory.getSketchPointHoverSpriteMaterial();
            }
            throw new Error(`椭圆轴端控制点仅支持移入或选中材质状态：${state}`);
        }

        if (object instanceof SketchPointObject && object.pointKind === SketchPointKind.ControlPoint) {
            if (state === GeometryInteractionState.Selected) {
                return PointsMaterialFactory.getSketchControlPointSelectedSpriteMaterial();
            }
            if (state === GeometryInteractionState.Hover || source === MaterialApplySource.LinkedLine) {
                return PointsMaterialFactory.getSketchControlPointHoverSpriteMaterial();
            }
            throw new Error(`控制点仅支持移入或选中材质状态：${state}`);
        }

        //------ 模型投影临时捕捉点 开始------
        // 可见模型边的端点、中点、中心与象限投影统一使用绿色材质，与红色草图原点做视觉区分。
        if (object instanceof SketchPointObject && object.isSketchSessionReferencePoint) {
            switch (state) {
                case GeometryInteractionState.Selected:
                    return PointsMaterialFactory.getDrawingSketchReferencePointSelectedSpriteMaterial();
                case GeometryInteractionState.Hover:
                    return PointsMaterialFactory.getDrawingSketchReferencePointHoverSpriteMaterial();
                case GeometryInteractionState.Creating:
                case GeometryInteractionState.Default:
                    return PointsMaterialFactory.getDrawingSketchReferencePointSpriteMaterial();
                default:
                    throw new Error(`未支持的草图会话参考点状态：${state}`);
            }
        }
        //------ 模型投影临时捕捉点 结束------

        if (object instanceof SketchPointObject && object.isDrawingSketchOrigin) {
            switch (state) {
                case GeometryInteractionState.Selected:
                    return PointsMaterialFactory.getDrawingSketchOriginSelectedSpriteMaterial();
                case GeometryInteractionState.Hover:
                    return PointsMaterialFactory.getDrawingSketchOriginHoverSpriteMaterial();
                case GeometryInteractionState.Creating:
                case GeometryInteractionState.Default:
                    return PointsMaterialFactory.getDrawingSketchOriginSpriteMaterial();
                default:
                    throw new Error(`未支持的草图会话原点投影状态：${state}`);
            }
        }

        if (object instanceof SketchPointObject && object.pointKind === SketchPointKind.SketchPoint) {
            switch (state) {
                case GeometryInteractionState.Creating:
                    return PointsMaterialFactory.getSketchPointSpriteMaterial();
                case GeometryInteractionState.Selected:
                    return PointsMaterialFactory.getSketchPointSelectedSpriteMaterial();
                case GeometryInteractionState.Hover:
                    return PointsMaterialFactory.getSketchPointHoverSpriteMaterial();
                case GeometryInteractionState.Default:
                    return PointsMaterialFactory.getSketchPointDefaultSpriteMaterial();
                default:
                    throw new Error(`未支持的草图点状态：${state}`);
            }
        }

        if (state === GeometryInteractionState.Selected) {
            return PointsMaterialFactory.getSketchPointSelectedSpriteMaterial();
        }
        if (state === GeometryInteractionState.Hover || source === MaterialApplySource.LinkedLine) {
            return PointsMaterialFactory.getSketchPointHoverSpriteMaterial();
        }
        if (state === GeometryInteractionState.Creating) {
            return PointsMaterialFactory.getSketchPointSpriteMaterial();
        }
        if (state === GeometryInteractionState.Default) {
            return PointsMaterialFactory.getSketchPointDefaultSpriteMaterial();
        }

        throw new Error(`未支持的点材质状态：${state}`);
    }

    /**
     * 创建默认态点材质。
     * @param object 目标点对象。
     * @returns 默认态点材质。
     */
    private createDefaultPointMaterial(
        object: SketchPointObject | FeaturePoint,
        context: InteractionVisualStateContext,
    ): THREE.SpriteNodeMaterial {
        if (isFeaturePoint(object)) {
            return object.getInteractionMaterial('default');
        }

        if (this.shouldUseReferencePreviewDefaultMaterial(object)) {
            return object.pointKind === SketchPointKind.ControlPoint
                ? PointsMaterialFactory.getReferencePreviewControlPointMaterial()
                : PointsMaterialFactory.getReferencePreviewMaterial();
        }

        if (this.shouldUseReferenceSourceHighlightDefaultMaterial(object)) {
            return object.pointKind === SketchPointKind.ControlPoint
                ? PointsMaterialFactory.getReferenceSourceHighlightControlPointMaterial()
                : PointsMaterialFactory.getReferenceSourceHighlightMaterial();
        }

        if (this.shouldUseInactiveSketchDefaultMaterial(object, context)) {
            if (object.isCurveCenterControlPoint) {
                return object.isFixed()
                    ? PointsMaterialFactory.getCurveCenterPointFixedInactiveSpriteMaterial()
                    : PointsMaterialFactory.getCurveCenterPointInactiveSpriteMaterial();
            }
            return object.isEllipseAxisControlPoint()
                ? PointsMaterialFactory.getEllipseAxisControlPointInactiveSpriteMaterial()
                : PointsMaterialFactory.getInactiveMaterial();
        }

        const isFixedPoint = object.isFixed();
        //------ 模型投影临时捕捉点 开始------
        // 默认态也必须保留绿色参考点材质，避免交互状态刷新后退回普通草图点样式。
        if (object.isSketchSessionReferencePoint) {
            return PointsMaterialFactory.getDrawingSketchReferencePointSpriteMaterial();
        }
        //------ 模型投影临时捕捉点 结束------
        if (object.isDrawingSketchOrigin) {
            return PointsMaterialFactory.getDrawingSketchOriginSpriteMaterial();
        }
        if (object.isCurveCenterControlPoint) {
            return isFixedPoint
                ? PointsMaterialFactory.getCurveCenterPointFixedSpriteMaterial()
                : PointsMaterialFactory.getCurveCenterPointSpriteMaterial();
        }
        if (object.isEllipseAxisControlPoint()) {
            return isFixedPoint
                ? PointsMaterialFactory.getSketchPointFixedDefaultSpriteMaterial()
                : PointsMaterialFactory.getSketchPointDefaultSpriteMaterial();
        }
        if (object.pointKind === SketchPointKind.ControlPoint) {
            return isFixedPoint
                ? PointsMaterialFactory.getSketchControlPointFixedSpriteMaterial()
                : PointsMaterialFactory.getSketchControlPointSpriteMaterial();
        }

        return isFixedPoint
            ? PointsMaterialFactory.getSketchPointFixedDefaultSpriteMaterial()
            : PointsMaterialFactory.getSketchPointDefaultSpriteMaterial();
    }

    /**
     * 判断当前对象默认态是否应使用实体引用预览材质。
     *
     * @param object 目标对象。
     * @returns 命中实体引用预览标记时返回 true。
     */
    private shouldUseReferencePreviewDefaultMaterial(object: THREE.Object3D): boolean {
        return isSketchEntityReferencePreview(object);
    }

    /**
     * 判断当前对象默认态是否应使用实体引用来源高亮材质。
     *
     * @param object 目标对象。
     * @returns 命中实体引用来源高亮标记时返回 true。
     */
    private shouldUseReferenceSourceHighlightDefaultMaterial(object: THREE.Object3D): boolean {
        return isSketchEntityReferenceSourceHighlight(object);
    }

    /**
     * 创建特征面材质。
     * @param object 特征面对象。
     * @param state 当前交互状态。
     * @returns 特征面材质。
     */
    private createFeatureFaceMaterial(
        object: FeatureFace,
        state: GeometryInteractionState,
    ): THREE.MeshBasicMaterial {
        // Convert Entities 会清空真实 selectedObjects，因此默认态需要服从命令来源高亮标记。
        const materialState = state === GeometryInteractionState.Selected
            || (
                state === GeometryInteractionState.Default
                && this.shouldUseReferenceSourceHighlightDefaultMaterial(object)
            )
            ? FeatureFaceMaterialState.Selected
            : state === GeometryInteractionState.Hover
                ? FeatureFaceMaterialState.Hover
                : FeatureFaceMaterialState.Default;
        const cacheKey = `feature-face:${materialState}`;
        const cachedMaterial = this.runtime.stateMeshMaterialCache.get(cacheKey);
        if (cachedMaterial instanceof THREE.MeshBasicMaterial) {
            return cachedMaterial;
        }

        /**
         * 材质层统一维护不同状态的 depthTest/depthWrite 语义，状态机只负责状态映射；
         * 实例化模型的可见 FeatureFace 会在状态计算后投影到正式实例槽，避免独立 Mesh 矩阵参与共面竞争。
         */
        const material = markSharedInteractionMaterial(createFeatureFaceMaterial(materialState));
        this.runtime.stateMeshMaterialCache.set(cacheKey, material);
        return material;
    }

    /**
     * 判断线对象默认态是否应使用固定黑色材质。
     * @param object 目标线对象。
     * @returns 需要使用固定默认材质返回 true，否则返回 false。
     */
    private hasFixedDefaultLineState(object: THREE.Line): boolean {
        if (!(object instanceof AbstractSketchEdgeObject)) {
            return false;
        }
        return object.constraints.hasType(SketchRelationKind.Fix);
    }

    /**
     * 根据对象材质推断外部状态提示。
     * @param object 目标对象。
     * @param material 材质。
     * @returns 推断状态。
     */
    private resolveExternalStateHintByMaterial(
        object: THREE.Mesh | THREE.Line | THREE.Sprite,
        material: THREE.Material | THREE.Material[],
    ): GeometryInteractionState {
        if (!(object instanceof SketchPointObject)) {
            return GeometryInteractionState.Default;
        }
        if (object.pointKind !== SketchPointKind.SketchPoint) {
            return GeometryInteractionState.Default;
        }
        if (Array.isArray(material)) {
            return GeometryInteractionState.Default;
        }

        const createdMaterial = PointsMaterialFactory.getSketchPointSpriteMaterial();
        if (material === createdMaterial) {
            return GeometryInteractionState.Creating;
        }
        return GeometryInteractionState.Default;
    }

    /**
     * 判断当前对象是否应使用非活动草图默认材质。
     *
     * 说明：
     * 1. 只在草图编辑相关模式中启用非活动降权，避免默认模式或参考要素模式误伤；
     * 2. 只有对象属于某个草图组且该草图组不是当前活动草图组时，才返回 true；
     * 3. 这里仅决定“默认态材质”的选型，hover / selected / creating 仍走原有状态分支。
     *
     * @param object 目标对象。
     * @param context 当前刷新上下文。
     * @returns 是否应该应用非活动默认材质。
     */
    private shouldUseInactiveSketchDefaultMaterial(
        object: THREE.Object3D,
        context: InteractionVisualStateContext,
    ): boolean {
        if (!this.isSketchVisualMode(context.modeContext.mode)) {
            return false;
        }

        const activeSketchGroupId = this.viewer.sketchGroupManager?.activeSketchGroup?.uuid ?? null;
        if (!activeSketchGroupId) {
            return false;
        }

        const sketchGroupId = findAncestorSketchGroupId(object);
        return sketchGroupId !== null && sketchGroupId !== activeSketchGroupId;
    }

    /**
     * 判断当前模式是否属于草图编辑视觉态。
     *
     * @param mode 当前 Viewer 模式。
     * @returns 是否属于草图编辑视觉态。
     */
    private isSketchVisualMode(mode: ViewerMode): boolean {
        const isSketchEditMode = mode === ViewerMode.Sketch
            || mode === ViewerMode.SketchDrawing
            || mode === ViewerMode.SketchTrim
            //------ 智能尺寸 开始------
            // 智能尺寸是草图内命令会话，视觉态与草图编辑一致（活动草图正常着色、非活动置灰）。
            || mode === ViewerMode.SketchDimension
            //------ 智能尺寸 结束------
            //------ 等距实体 开始------
            // 等距实体是草图内命令会话，视觉态与草图编辑一致（活动草图正常着色、非活动置灰）。
            || mode === ViewerMode.SketchOffset;
            //------ 等距实体 结束------

        //------ 草图实体变换非活动草图置灰 开始------
        /**
         * Move / Copy / Rotate 仍处于当前草图编辑会话内。
         * 这里必须继续使用非活动草图默认材质，否则清空旧选择并刷新状态时，
         * 其他草图会被重新铺成当前草图颜色。
         */
        const isSketchEntitiesTransformMode = mode === ViewerMode.SketchMoveEntities
            || mode === ViewerMode.SketchCopyEntities
            || mode === ViewerMode.SketchRotateEntities
            || mode === ViewerMode.SketchCircularPattern
            || mode === ViewerMode.SketchMirrorEntities;
        //------ 草图实体变换非活动草图置灰 结束------

        return isSketchEditMode || isSketchEntitiesTransformMode;
    }

    /**
     * 判断对象是否可应用当前模式状态。
     * @param object 目标对象。
     * @param context 当前刷新上下文。
     * @returns 是否可应用。
     */
    private canApplyToObject(object: THREE.Object3D, context: InteractionVisualStateContext): boolean {
        if (context.modeContext.mode === ViewerMode.Default) {
            return this.isStateManagedObject(object);
        }

        if (
            context.modeContext.mode === ViewerMode.Assembly
            || context.modeContext.mode === ViewerMode.MoveComponent
            //------ 线性零部件阵列 开始------
            || context.modeContext.mode === ViewerMode.LinearComponentPattern
            //------ 线性零部件阵列 结束------
        ) {
            return this.isStateManagedObject(object);
        }

        if (
            context.modeContext.mode === ViewerMode.ReferencePlane
            //------ 装配体模式下基准点线面 开始------
            || context.modeContext.mode === ViewerMode.AssemblyReferencePlane
            //------ 装配体模式下基准点线面 结束------
        ) {
            return this.isReferencePlaneManagedObject(object);
        }

        if (
            context.modeContext.mode === ViewerMode.ReferenceAxis
            //------ 装配体模式下基准点线面 开始------
            || context.modeContext.mode === ViewerMode.AssemblyReferenceAxis
            //------ 装配体模式下基准点线面 结束------
        ) {
            return this.isReferenceAxisManagedObject(object);
        }

        if (
            context.modeContext.mode === ViewerMode.ReferencePoint
            //------ 装配体模式下基准点线面 开始------
            || context.modeContext.mode === ViewerMode.AssemblyReferencePoint
            //------ 装配体模式下基准点线面 结束------
        ) {
            return this.isReferencePointManagedObject(object);
        }

        if (
            context.modeContext.mode === ViewerMode.Sketch
            || context.modeContext.mode === ViewerMode.SketchDrawing
            || context.modeContext.mode === ViewerMode.SketchMoveEntities
            || context.modeContext.mode === ViewerMode.SketchCopyEntities
            || context.modeContext.mode === ViewerMode.SketchRotateEntities
            || context.modeContext.mode === ViewerMode.SketchCircularPattern
            || context.modeContext.mode === ViewerMode.SketchMirrorEntities
            || context.modeContext.mode === ViewerMode.SketchTrim
            //------ 智能尺寸 开始------
            // 漏登记会让本方法落到末尾 throw，整个视觉刷新中断——
            // 表现为智能尺寸模式下 hover/选中材质全都不生效。
            || context.modeContext.mode === ViewerMode.SketchDimension
            //------ 智能尺寸 结束------
            //------ 等距实体 开始------
            // 漏登记会让本方法落到末尾 throw，整个视觉刷新中断——
            // 表现为等距实体模式下 hover/选中材质全都不生效。
            || context.modeContext.mode === ViewerMode.SketchOffset
            //------ 等距实体 结束------
        ) {
            return this.isSketchStateObject(object);
        }

        throw new Error(`未支持的交互模式：${context.modeContext.mode}`);
    }

    /**
     * 判断对象是否为草图状态机允许处理的对象。
     * @param object 待判断对象。
     * @returns 是否为草图状态机对象。
     */
    private isSketchStateObject(
        object: THREE.Object3D,
    ): object is SketchLineObject | SketchSplineObject | SketchStyleSplineObject | SketchParabolaObject | SketchCircleObject | SketchArcObject | SketchEllipseObject | SketchPointObject {
        return isSketchLineObject(object)
            || isSketchSplineObject(object)
            || isSketchStyleSplineObject(object)
            || isSketchParabolaObject(object)
            || isSketchCircleObject(object)
            || isSketchArcObject(object)
            || isSketchEllipseObject(object)
            || isSketchPointObject(object);
    }

    /**
     * 判断对象是否为当前交互状态系统允许处理的对象。
     * 说明：
     * 1. 默认模式需要同时覆盖草图对象与特征对象；
     * 2. 草图模式仍只处理草图对象，避免特征实体干扰草图编辑；
     * 3. 该守卫是默认模式下点/线/面 hover 与 click 高亮恢复的核心入口。
     * @param object 待判断对象。
     * @returns 是否为可管理对象。
     */
    private isStateManagedObject(
        object: THREE.Object3D,
    ): object is SketchLineObject | SketchSplineObject | SketchStyleSplineObject | SketchParabolaObject | SketchCircleObject | SketchArcObject | SketchEllipseObject | SketchPointObject | FeatureFace | FeatureLine | FeaturePoint | FeatureResultMesh {
        return this.isSketchStateObject(object)
            || isFeatureFace(object)
            || isFeatureLine(object)
            || isFeaturePoint(object)
            || isFeatureResultMesh(object);
    }

    /**
     * 判断对象是否为基准轴命令可管理对象。
     * @param object 待判断对象。
     * @returns 是否为基准轴可管理对象。
     */
    private isReferenceAxisManagedObject(
        object: THREE.Object3D,
    ): object is FeatureFace | FeatureLine | FeaturePoint {
        return isFeatureFace(object)
            || isFeatureLine(object)
            || isFeaturePoint(object);
    }

    /**
     * 判断对象是否为基准面命令可管理对象。
     * @param object 待判断对象。
     * @returns 是否为基准面可管理对象。
     */
    private isReferencePlaneManagedObject(
        object: THREE.Object3D,
    ): object is FeatureFace | FeatureLine | FeaturePoint {
        return isFeatureFace(object)
            || isFeatureLine(object)
            || isFeaturePoint(object);
    }

    /**
     * 判断对象是否为基准点命令可管理对象。
     * @param object 待判断对象。
     * @returns 是否为基准点可管理对象。
     */
    private isReferencePointManagedObject(
        object: THREE.Object3D,
    ): object is FeatureFace | FeatureLine | FeaturePoint {
        return isFeatureFace(object)
            || isFeatureLine(object)
            || isFeaturePoint(object);
    }

    /**
     * 根据状态解析线颜色。
     * @param state 状态类型。
     * @param context 当前刷新上下文。
     * @returns 线颜色。
     */
    private resolveLineColorByState(
        state: GeometryInteractionState,
        context: InteractionVisualStateContext,
    ): THREE.Color {
        if (context.modeContext.mode === ViewerMode.SketchDrawing) {
            return AbstractInteractionVisualState.SKETCH_ACTIVE_ORANGE.clone();
        }
        if (state === GeometryInteractionState.Hover) {
            return new THREE.Color(HOVER_EDGE_COLOR);
        }
        return new THREE.Color(SELECTED_EDGE_COLOR);
    }
}
